import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useErrorHandler from './useErrorHandler';
import useNotification from './useNotification';

type FeedbackHandler = <T>(
	action: () => Promise<T>,
	successMessage?: string,
	errorMessage?: string,
) => Promise<T | undefined>;
/**
 * useFeedbackHandler Hook
 *
 * This custom hook combines success and error handling into one reusable function.
 * It reuses the existing `useNotification` and `useErrorHandler` hooks to display feedback.
 *
 * @returns A function that wraps around the given logic and handles feedback notifications.
 *
 * @example
 * const handleFeedback = useFeedbackHandler();
 * handleFeedback(asyncFunction, 'Success Message', 'Error occurred!');
 */
const useFeedbackHandler = (): FeedbackHandler => {
	const { t } = useTranslation();

	const showNotification = useNotification();
	const showError = useErrorHandler();

	const handleFeedback = useCallback<FeedbackHandler>(
		async (action, successMessage, errorMessage) => {
			try {
				const result = await action();

				showNotification('success', successMessage || t('shared.success'));

				return result;
			} catch (e) {
				console.error(e);
				if (errorMessage) {
					showNotification('error', errorMessage || t('shared.somethingWentWrong'));
				} else {
					showError(e.errors);
				}

				return undefined;
			}
		},
		[showNotification, showError],
	);

	return handleFeedback;
};

export default useFeedbackHandler;
